/**
 * Aerial of one example detached house, with a panel array that redraws
 * as the count changes. The tiled roof is two slopes split by a ridge.
 *
 * ROOF is the usable tile area in image pixels. Local +y points down the
 * photo toward the patio, which is the south slope; the rear half is north.
 * Each slope holds a 6×2 array (12 panels). Panels fill the south slope
 * first, then the north slope, and never cross the ridge.
 *
 * Panel footprint is constant: the size that fits that 6×2 array on one
 * slope. Changing the count adds or removes panels; it does not stretch them.
 */
const AERIAL = { width: 1390, height: 1132, src: "/example-property-aerial.webp" };

const ROOF = { cx: 726.5, cy: 460, w: 314, h: 342, rotate: 38 };
const RIDGE_GAP = 18;
const FACET_COLS = 6;
const FACET_ROWS = 2;
const FACET_CAPACITY = FACET_COLS * FACET_ROWS;
const FACET_H = (ROOF.h - RIDGE_GAP) / 2;
const PANEL_GAP = 6;
const PANEL_PAD = 8;
const PANEL_W = (ROOF.w - PANEL_PAD * 2 - PANEL_GAP * (FACET_COLS - 1)) / FACET_COLS;
const PANEL_H = (FACET_H - PANEL_PAD * 2 - PANEL_GAP * (FACET_ROWS - 1)) / FACET_ROWS;

// The previous drawing used a 640-wide viewBox. Scale strokes so the lime
// outline keeps the same weight on this 1390-wide photo.
const STROKE = AERIAL.width / 640;

// South slope starts on the patio side of the ridge.
const SOUTH_Y = FACET_H + RIDGE_GAP;
const NORTH_Y = 0;

function panelsOnFacet(count: number, originY: number) {
  if (count <= 0) return [];
  const cols = count <= 3 ? count : count <= 6 ? 3 : count <= 8 ? 4 : count <= 10 ? 5 : FACET_COLS;
  const rows = Math.ceil(count / cols);
  const arrayW = cols * PANEL_W + PANEL_GAP * (cols - 1);
  const arrayH = rows * PANEL_H + PANEL_GAP * (rows - 1);
  const originX = (ROOF.w - arrayW) / 2;
  const arrayY = originY + (FACET_H - arrayH) / 2;

  return Array.from({ length: count }, (_, index) => {
    const col = index % cols;
    const row = Math.floor(index / cols);
    const inRow = Math.min(cols, count - row * cols);
    const rowShift = ((cols - inRow) * (PANEL_W + PANEL_GAP)) / 2;
    return {
      x: originX + rowShift + col * (PANEL_W + PANEL_GAP),
      y: arrayY + row * (PANEL_H + PANEL_GAP),
    };
  });
}

export default function ExampleRoofLayout({ panels }: { panels: number }) {
  const count = Math.max(0, Math.min(FACET_CAPACITY * 2, Math.round(panels)));
  const placed = [
    ...panelsOnFacet(Math.min(count, FACET_CAPACITY), SOUTH_Y),
    ...panelsOnFacet(Math.max(0, count - FACET_CAPACITY), NORTH_Y),
  ];

  return (
    <div
      className="relative"
      role="img"
      aria-label={`Aerial view of an example detached house with ${count} solar panels`}
    >
      <img
        src={AERIAL.src}
        alt=""
        width={AERIAL.width}
        height={AERIAL.height}
        className="block h-auto w-full"
      />
      <svg
        viewBox={`0 0 ${AERIAL.width} ${AERIAL.height}`}
        preserveAspectRatio="none"
        className="pointer-events-none absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        <g
          transform={`translate(${ROOF.cx} ${ROOF.cy}) rotate(${ROOF.rotate}) translate(${-ROOF.w / 2} ${-ROOF.h / 2})`}
        >
          {placed.map((panel, index) => (
            <g key={index}>
              <rect
                x={panel.x}
                y={panel.y}
                width={PANEL_W}
                height={PANEL_H}
                rx={1.5 * STROKE}
                fill="#191919"
                stroke="#d4ff00"
                strokeWidth={STROKE}
              />
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}
